import { logger } from "@/src/lib/logging/logger";
import { withDurationMetric } from "@/src/lib/metrics";
import { withRetry } from "@/src/lib/retry";
import { getSupabaseClient } from "@/src/lib/supabase";
import { RingCentralAuthStorageRow } from "@/src/lib/types/tables.types";

export const RINGCENTRAL_AUTH_STORAGE_TABLE_NAME = "ringcentral_auth_storage";

const fullSelect = "*";

function parseEntries(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};

  const out: Record<string, string> = {};

  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string") out[key] = value;
  }

  return out;
}

export const getRingCentralAuthStorageBySessionKey = withDurationMetric(
  async function getRingCentralAuthStorageBySessionKey(
    sessionKey: string
  ): Promise<{ entries: Record<string, string> } | null> {
    const { data, error } = await withRetry(`getBySessionKey from ${RINGCENTRAL_AUTH_STORAGE_TABLE_NAME}`, async () => {
      const supabaseClient = await getSupabaseClient();

      return supabaseClient
        .from(RINGCENTRAL_AUTH_STORAGE_TABLE_NAME)
        .select(fullSelect)
        .eq("session_key", sessionKey)
        .maybeSingle();
    });

    if (error) {
      logger.warn("Error loading RingCentral auth storage", {
        functionName: "getRingCentralAuthStorageBySessionKey",
        tableName: RINGCENTRAL_AUTH_STORAGE_TABLE_NAME,
        sessionKeyPrefix: sessionKey.slice(0, 8),
        error,
      });

      return null;
    }

    if (!data) {
      return null;
    }

    const row = data as RingCentralAuthStorageRow;

    return { entries: parseEntries(row.entries) };
  },
  "getRingCentralAuthStorageBySessionKey"
);

export const upsertRingCentralAuthStorage = withDurationMetric(async function upsertRingCentralAuthStorage(
  sessionKey: string,
  entries: Record<string, string>
): Promise<void> {
  const { error } = await withRetry(`upsert ${RINGCENTRAL_AUTH_STORAGE_TABLE_NAME}`, async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient.from(RINGCENTRAL_AUTH_STORAGE_TABLE_NAME).upsert(
      {
        session_key: sessionKey,
        entries,
      },
      { onConflict: "session_key" }
    );
  });

  if (error) {
    logger.warn("Error upserting RingCentral auth storage", {
      functionName: "upsertRingCentralAuthStorage",
      tableName: RINGCENTRAL_AUTH_STORAGE_TABLE_NAME,
      sessionKeyPrefix: sessionKey.slice(0, 8),
      error,
    });

    throw error;
  }
}, "upsertRingCentralAuthStorage");
